import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import { backendPost } from "@accredo/fe-shared/services/dataService.js";

const isFieldStaffRole = (user) =>
  user?.role === "kd_aussendienst" || user?.role === "kd_eingeschraenkt";
const isRestrictedAussendienst = (user) => user?.role === "kd_eingeschraenkt";
// Nur kd_verwaltung wird tatsächlich benachrichtigt (siehe
// NotificationService::notifyAllCustomerRoleAndPermission() - verlangt Rolle
// UND Permission), bei jeder anderen Rolle bliebe die Permission wirkungslos.
const isCustomerAdministrationRole = (user) => user?.role === "kd_verwaltung";
const receivesInternalReport = (user) =>
  !!user?.permissions?.includes("claim.customer.receive_pre_check_customer");

/**
 * Modal für die Benutzer-Tabelle der Kundendetailseite: Klick auf eine Zeile
 * öffnet dieses Modal, die Umschalter ändern nur lokalen State - die
 * eigentlichen Aktionen (staff-restricted/internal-recipient) werden erst
 * beim Klick auf "Speichern" ausgelöst.
 */
function CustomerUserActionsModal({ open, onClose, user, customerId, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [receivesReport, setReceivesReport] = useState(false);

  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    setRestricted(isRestrictedAussendienst(user));
    setReceivesReport(receivesInternalReport(user));
    setOpenCD(true);
    // eslint-disable-next-line
  }, [open, user?.id]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (isFieldStaffRole(user) && restricted !== isRestrictedAussendienst(user)) {
        await backendPost(
          `data/kundes/${customerId}/users/${user.id}/staff-restricted`,
          {
            toSend: { user_id: user.id, restricted },
            label: "Außendienst-Berechtigung",
            enqueueSnackbar,
          }
        );
      }

      if (
        isCustomerAdministrationRole(user) &&
        receivesReport !== receivesInternalReport(user)
      ) {
        await backendPost(
          `data/kundes/${customerId}/users/${user.id}/internal-recipient`,
          {
            toSend: { user_id: user.id, enabled: receivesReport },
            label: "Empfänger interner Schadenreport",
            enqueueSnackbar,
          }
        );
      }

      enqueueSnackbar("Änderungen gespeichert.", { variant: "success" });
      onSuccess?.();
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setTca({
      title: user ? user.full_name || user.email : "Benutzer",
      maxWidth: "sm",
      content: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {isFieldStaffRole(user) ? (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={restricted}
                    onChange={(e) => setRestricted(e.target.checked)}
                  />
                }
                label="Außendienstmitarbeiter (eingeschränkt)"
              />
              <Typography variant="body2" color="text.secondary">
                Die eingereichten Schäden des Benutzers müssen durch eine Kundeninterne Prüfung.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Außendienst-Berechtigung nur für Außendienst-Rollen umschaltbar.
            </Typography>
          )}
          {isCustomerAdministrationRole(user) ? (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={receivesReport}
                    onChange={(e) => setReceivesReport(e.target.checked)}
                  />
                }
                label="Empfänger interner Schadenreport"
              />
              <Typography variant="body2" color="text.secondary">
                Der Benutzer erhält Benachrichtigung über intern eingereichten Schäden.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Empfänger interner Schadenreport nur für Benutzer mit der Rolle
              "Verwaltung" umschaltbar.
            </Typography>
          )}
        </Box>
      ),
      actions: (
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            Speichern
          </Button>
        </>
      ),
    });
    // eslint-disable-next-line
  }, [user, restricted, receivesReport, isLoading]);

  return (
    <>
      <Backdrop
        open={isLoading}
        style={{ zIndex: 1300, flexDirection: "column" }}
      >
        <CircularProgress style={{ marginBottom: 16 }} />
      </Backdrop>
      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        tca={tca}
        onClose={onClose}
      />
    </>
  );
}

CustomerUserActionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default CustomerUserActionsModal;
